import { forwardRef, useId } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Check, CircleAlert } from 'lucide-react';
import { cn } from './cn';

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  /** Label rendered to the right of the box. Clicking it toggles the checkbox. */
  label?: React.ReactNode;
  /** Error message (string) or error flag (boolean). Renders the danger (red) validation treatment. */
  error?: string | boolean;
  /** Secondary helper text rendered under the label. */
  description?: React.ReactNode;
}

/**
 * Checkbox — replaces MUI <Checkbox /> (+ <FormControlLabel />).
 * A visually-hidden native input drives a custom-styled box for full a11y and
 * keyboard support. Forwards ref to the underlying <input>.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, error, description, className, disabled, id, ...props }, ref) => {
    const reactId = useId();
    const inputId = id ?? reactId;
    const hasError = Boolean(error);
    const errorText = typeof error === 'string' ? error : undefined;
    const descId = description ? `${inputId}-desc` : undefined;
    const errId = errorText ? `${inputId}-err` : undefined;
    const describedBy = [descId, errId].filter(Boolean).join(' ') || undefined;

    return (
      <div className={cn('flex flex-col gap-1', className)}>
        <label
          htmlFor={inputId}
          className={cn(
            'group inline-flex cursor-pointer items-start gap-2.5 select-none',
            disabled && 'cursor-not-allowed opacity-60',
          )}
        >
          <span className="relative flex shrink-0 items-center">
            <input
              ref={ref}
              id={inputId}
              type="checkbox"
              disabled={disabled}
              aria-invalid={hasError || undefined}
              aria-describedby={describedBy}
              className="peer sr-only"
              {...props}
            />
            <span
              aria-hidden="true"
              className={cn(
                'flex h-5 w-5 items-center justify-center rounded-md border bg-surface',
                'transition-all duration-200',
                'peer-checked:border-primary peer-checked:bg-primary',
                'peer-focus-visible:ring-2 peer-focus-visible:ring-primary/40 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-canvas',
                hasError
                  ? 'border-danger ring-2 ring-danger/25'
                  : 'border-line group-hover:border-primary/60 peer-checked:group-hover:border-primary',
              )}
            >
              <AnimatePresence initial={false}>
                {props.checked && (
                  <motion.span
                    key="check"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 28 }}
                    className="flex text-white"
                  >
                    <Check size={14} strokeWidth={3} />
                  </motion.span>
                )}
              </AnimatePresence>
              {/* Uncontrolled fallback: native :checked drives the icon when `checked` prop is absent. */}
              {props.checked === undefined && (
                <Check
                  size={14}
                  strokeWidth={3}
                  className="text-white opacity-0 transition-opacity duration-150 peer-checked:opacity-100"
                />
              )}
            </span>
          </span>

          {(label || description) && (
            <span className="flex min-w-0 flex-col gap-0.5 pt-px">
              {label && <span className="text-sm leading-snug text-content">{label}</span>}
              {description && (
                <span id={descId} className="text-xs leading-snug text-muted">
                  {description}
                </span>
              )}
            </span>
          )}
        </label>

        {errorText && (
          <p id={errId} className="flex items-start gap-1.5 text-xs font-medium text-danger">
            <CircleAlert size={13} strokeWidth={2.5} className="mt-px shrink-0" aria-hidden="true" />
            <span>{errorText}</span>
          </p>
        )}
      </div>
    );
  },
);

Checkbox.displayName = 'Checkbox';
